import React, { useState } from 'react';
import { ArrowLeft, Utensils, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const FoodEmojis = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const foodEmojis = {
    meals: ['🍕', '🍔', '🌭', '🥪', '🌮', '🌯', '🥙', '🍜', '🍝', '🍛'],
    fruits: ['🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🥝'],
    sweets: ['🍦', '🍧', '🍨', '🍩', '🍪', '🎂', '🧁', '🥧', '🍫', '🍬'],
    drinks: ['☕', '🍵', '🧃', '🥤', '🧋', '🍶', '🍷', '🍸', '🍹', '🍺']
  };

  const allEmojis = Object.values(foodEmojis).flat().join('');

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>🍕🍔🌮🍜 Food Emojis Copy and Paste Collection 🍜🌮🍔🍕</title>
        <meta name="description" content={`【INSTANT COPY】 Get all food emojis! 🍕 Complete collection of food & drink emojis to copy and paste 🍔 One-click copy feature 🌭 Works everywhere! Updated for ${currentYear}`} />
        <link rel="canonical" href="https://emojicollection.net/food-emojis" />
        <meta name="keywords" content="food emojis copy and paste, food emoji collection, food symbols copy paste, restaurant emojis, meal emojis, drink emojis" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-500 mb-4">
                <Utensils className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Food Emojis Copy and Paste Collection
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get instant access to all food and drink emojis! Perfect for restaurants, food bloggers, and foodies. Updated for {currentYear} with the latest emojis.
              </p>
            </header>

            {Object.entries(foodEmojis).map(([category, emojis]) => (
              <section key={category} className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 capitalize">
                  {category} Emojis
                </h2>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-3xl mb-4">{emojis.join('')}</div>
                  <button
                    onClick={() => handleCopy(emojis.join(''))}
                    className={`inline-flex items-center px-4 py-2 rounded-lg ${
                      copied
                        ? 'bg-green-500 text-white'
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    } transition-colors`}
                  >
                    {copied ? (
                      <>
                        <Check className="mr-2" size={20} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2" size={20} />
                        Copy {category} Emojis
                      </>
                    )}
                  </button>
                </div>
              </section>
            ))}

            <section className="border-t border-gray-200 pt-8 mt-8">
              <h2 className="text-2xl font-semibold mb-6">Related Collections</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <a href="/drink-emojis" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="font-medium mb-2">Drink Emojis Collection 🍹</h3>
                  <p className="text-sm text-gray-600">Beverage emojis for every occasion</p>
                </a>
                <a href="/restaurant-emojis" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="font-medium mb-2">Restaurant Emojis 🍽️</h3>
                  <p className="text-sm text-gray-600">Perfect for restaurant menus and reviews</p>
                </a>
                <a href="/dessert-emojis" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="font-medium mb-2">Dessert Emojis 🍰</h3>
                  <p className="text-sm text-gray-600">Sweet treat emojis collection</p>
                </a>
                <a href="/fruit-emojis" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <h3 className="font-medium mb-2">Fruit Emojis 🍎</h3>
                  <p className="text-sm text-gray-600">Fresh fruit emoji collection</p>
                </a>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default FoodEmojis;